import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import { ArrowLeft, Truck, Plus, ChevronRight, Loader2, Wrench, Bell, X, Send, Building2, AlertTriangle, TrendingUp, TrendingDown, Calendar, Hash, Tag, Shield, DollarSign, History, Search, Receipt, Download, ChevronDown, ChevronUp, MapPin, Mail, Pencil, FileText, CreditCard, Banknote, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CATEGORY_LIST } from '@/constants/repairOptions';
import { STATUS_COLORS as REPAIR_STATUS_COLORS, formatStatus as formatRepairStatus } from '@/constants/statusColors';
import { STATES, STATE_COLORS, STATE_LABELS, TRUCK_STATUS_COLORS, TENANT_BADGES } from './customerConstants';
import { StateChangeModal, PaymentModal, AddUnitModal, ShareHealthModal, TenantModal, TierChangeModal } from './CustomerModals';
import BillingInfoCard from './components/BillingInfoCard';
import CustomerLoginCard from './components/CustomerLoginCard';

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unitFilter, setUnitFilter] = useState('all');
  const [unitSearch, setUnitSearch] = useState('');
  const [showStateModal, setShowStateModal] = useState(false);
  const [showTruckModal, setShowTruckModal] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [tenantChanging, setTenantChanging] = useState(false);
  const [tenantReason, setTenantReason] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  // Pricing tier state
  const [pricingTiers, setPricingTiers] = useState([]);
  const [customerTier, setCustomerTier] = useState(null);
  const [tierHistory, setTierHistory] = useState([]);
  const [showTierModal, setShowTierModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingForm, setBillingForm] = useState({ billing_mode: '', billing_rate: '', billing_units_limit: '' });
  const [savingBilling, setSavingBilling] = useState(false);
  const [selectedTierId, setSelectedTierId] = useState('');
  const [tierConfirmText, setTierConfirmText] = useState('');
  const [tierChanging, setTierChanging] = useState(false);

  // Alert settings
  const [alertSettings, setAlertSettings] = useState({ default_miles: 5000, default_days: 30, category_overrides: {} });
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [newOverrideCat, setNewOverrideCat] = useState('');

  // Health score
  const [healthScore, setHealthScore] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareMsg, setShareMsg] = useState('');
  const [sharing, setSharing] = useState(false);
  
  // Ops notes & payment methods
  const [opsNotes, setOpsNotes] = useState('');
  const [savingOpsNotes, setSavingOpsNotes] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ type: 'check', label: '', details: '' });
  const [savingPayment, setSavingPayment] = useState(false);

  // Unit invoice linking
  const [expandedUnit, setExpandedUnit] = useState(null);
  const [unitInvoices, setUnitInvoices] = useState({});

  const [newState, setNewState] = useState('');
  const [stateNote, setStateNote] = useState('');
  const [truckForm, setTruckForm] = useState({ unit_number: '', make: '', model: '', year: '', current_mileage: '', plate: '', unit_type: 'Truck' });
  const [saving, setSaving] = useState(false);
  const [showAccountDetails, setShowAccountDetails] = useState(false);
  const [billingSummary, setBillingSummary] = useState(null);
  const [sendingBillingLink, setSendingBillingLink] = useState(false);
  const [showJourneyModal, setShowJourneyModal] = useState(false);

  useEffect(() => { load(); loadCurrentUser(); loadPricingTiers(); }, [id]);

  const loadPricingTiers = async () => {
    try {
      const res = await api.get('/pricing-tiers');
      setPricingTiers(res.data.tiers || []);
    } catch (e) {
      console.error('Failed to load pricing tiers');
    }
  };

  const loadCustomerTier = async () => {
    try {
      const [tierRes, histRes] = await Promise.all([
        api.get(`/pricing-tiers/customer/${id}/tier`),
        api.get(`/pricing-tiers/customer/${id}/tier-history`),
      ]);
      setCustomerTier(tierRes.data.tier);
      setTierHistory(histRes.data.history || []);
    } catch (e) {
      console.error('Failed to load customer tier');
    }
  };

  const loadCurrentUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setCurrentUser(res.data);
    } catch (e) {
      console.error('Failed to load user');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, tRes, rRes] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get('/trucks', { params: { customer_id: id } }),
        api.get('/repairs', { params: { customer_id: id, limit: 10 } }),
      ]);
      setCustomer(cRes.data);
      setAlertSettings(cRes.data.alert_settings || { default_miles: 5000, default_days: 30, category_overrides: {} });
      setOpsNotes(cRes.data.ops_notes || '');
      setPaymentMethods(cRes.data.payment_methods || []);
      setTrucks(tRes.data);
      setRepairs(rRes.data.items);
    } catch (e) {
      toast.error('Failed to load customer');
    } finally {
      setLoading(false);
    }
    // Load health score separately
    setScoreLoading(true);
    api.get(`/health-scores/${id}`).then(r => setHealthScore(r.data)).catch(() => {}).finally(() => setScoreLoading(false));
    // Load pricing tier data
    loadCustomerTier();
    // Load billing summary (for top banner)
    api.get(`/billing/customer/${id}`).then(r => setBillingSummary(r.data)).catch(() => setBillingSummary(null));
  };

  const sendBillingLinkFromBanner = async () => {
    setSendingBillingLink(true);
    try {
      const r = await api.post(`/billing/customer/${id}/send-link`, {});
      if (r.data.emailed === false) {
        toast.warning(`Link created but the email to ${r.data.email} could not be sent — copy the link from the Billing card instead`);
      } else {
        toast.success(`Setup link sent to ${r.data.email}`);
      }
      api.get(`/billing/customer/${id}`).then(res => setBillingSummary(res.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send link');
    } finally {
      setSendingBillingLink(false);
    }
  };

  const handleChangeTier = async () => {
    if (tierConfirmText !== 'CONFIRM') {
      toast.error('You must type CONFIRM to change the pricing tier');
      return;
    }
    setTierChanging(true);
    try {
      await api.patch(`/pricing-tiers/customer/${id}`, {
        pricing_tier_id: selectedTierId,
        confirm: 'CONFIRM'
      });
      toast.success('Pricing tier updated');
      setShowTierModal(false);
      setTierConfirmText('');
      setSelectedTierId('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change pricing tier');
    } finally {
      setTierChanging(false);
    }
  };

  const handleChangeTenant = async (newTenant) => {
    setTenantChanging(true);
    try {
      await api.patch(`/customers/${id}/tenant`, { 
        tenant_group: newTenant,
        reason: tenantReason 
      });
      toast.success(`Customer moved to ${newTenant}`);
      setShowTenantModal(false);
      setTenantReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change tenant');
    } finally {
      setTenantChanging(false);
    }
  };

  const handleStateChange = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/customers/${id}/onboarding`, { new_state: newState, note: stateNote });
      toast.success('Onboarding state updated');
      setShowStateModal(false);
      setStateNote('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTruck = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/trucks', {
        ...truckForm, customer_id: id,
        year: truckForm.year ? Number(truckForm.year) : undefined,
        current_mileage: truckForm.current_mileage ? Number(truckForm.current_mileage) : 0,
      });
      toast.success('Unit added!');
      setShowTruckModal(false);
      setTruckForm({ unit_number: '', make: '', model: '', year: '', current_mileage: '', plate: '', unit_type: 'Truck' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleShareScore = async () => {
    setSharing(true);
    try {
      await api.post(`/health-scores/${id}/share`, { message: shareMsg });
      toast.success('Fleet Health Report sent!');
      setShowShareModal(false);
      setShareMsg('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSharing(false);
    }
  };

  const saveAlertSettings = async () => {
    setSavingAlerts(true);
    try {
      const payload = {
        default_miles: Number(alertSettings.default_miles) || 5000,
        default_days: Number(alertSettings.default_days) || 30,
        category_overrides: Object.fromEntries(
          Object.entries(alertSettings.category_overrides || {}).map(([k, v]) => [k, {
            miles: Number(v.miles) || Number(alertSettings.default_miles) || 5000,
            days: Number(v.days) || Number(alertSettings.default_days) || 30,
          }])
        )
      };
      await api.patch(`/customers/${id}/alert-settings`, payload);
      toast.success('Alert settings saved!');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSavingAlerts(false);
    }
  };

  const addCategoryOverride = () => {
    if (!newOverrideCat || alertSettings.category_overrides?.[newOverrideCat]) return;
    setAlertSettings(p => ({
      ...p,
      category_overrides: { ...p.category_overrides, [newOverrideCat]: { miles: p.default_miles, days: p.default_days } }
    }));
    setNewOverrideCat('');
  };

  const removeCategoryOverride = (cat) => {
    setAlertSettings(p => {
      const next = { ...p.category_overrides };
      delete next[cat];
      return { ...p, category_overrides: next };
    });
  };

  const handleSaveOpsNotes = async () => {
    setSavingOpsNotes(true);
    try {
      await api.put(`/customers/${id}/ops-notes`, { ops_notes: opsNotes });
      toast.success('Internal notes saved');
    } catch { toast.error('Failed to save notes'); }
    finally { setSavingOpsNotes(false); }
  };

  // ─── Addresses (physical / mailing / additional) ───
  const [showAddrModal, setShowAddrModal] = useState(false);
  const [addrForm, setAddrForm] = useState({
    address: '', city: '', state: '', zip_code: '',
    mailing_same_as_physical: true,
    mailing_street_address: '', mailing_city: '', mailing_state: '', mailing_zip_code: '',
    additional_addresses: [],
  });
  const [savingAddr, setSavingAddr] = useState(false);

  const openAddressEditor = () => {
    setAddrForm({
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      zip_code: customer.zip_code || '',
      mailing_same_as_physical: customer.mailing_same_as_physical ?? true,
      mailing_street_address: customer.mailing_street_address || '',
      mailing_city: customer.mailing_city || '',
      mailing_state: customer.mailing_state || '',
      mailing_zip_code: customer.mailing_zip_code || '',
      additional_addresses: customer.additional_addresses || [],
    });
    setShowAddrModal(true);
  };

  const saveAddresses = async () => {
    setSavingAddr(true);
    try {
      const payload = { ...addrForm };
      if (payload.mailing_same_as_physical) {
        payload.mailing_street_address = payload.address;
        payload.mailing_city = payload.city;
        payload.mailing_state = payload.state;
        payload.mailing_zip_code = payload.zip_code;
      }
      await api.put(`/customers/${id}`, payload);
      setCustomer((c) => ({ ...c, ...payload }));
      toast.success('Addresses updated');
      setShowAddrModal(false);
    } catch (e) {
      toast.error('Failed to update addresses');
    } finally {
      setSavingAddr(false);
    }
  };

  // ─── Full Application View ───
  const [showAppModal, setShowAppModal] = useState(false);
  const [appData, setAppData] = useState(null);
  const [loadingApp, setLoadingApp] = useState(false);

  const loadApplication = async () => {
    setLoadingApp(true);
    try {
      const r = await api.get(`/customers/${id}/application`);
      setAppData(r.data);
    } catch {
      toast.error('Could not load application');
    } finally {
      setLoadingApp(false);
    }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.type || !paymentForm.label) { toast.error('Type and label required'); return; }
    setSavingPayment(true);
    try {
      const res = await api.post(`/customers/${id}/payment-methods`, paymentForm);
      setPaymentMethods(prev => [...prev, res.data]);
      setShowPaymentModal(false);
      setPaymentForm({ type: 'check', label: '', details: '' });
      toast.success('Payment method added');
    } catch { toast.error('Failed to add payment method'); }
    finally { setSavingPayment(false); }
  };

  const handleDeletePayment = async (methodId) => {
    try {
      await api.delete(`/customers/${id}/payment-methods/${methodId}`);
      setPaymentMethods(prev => prev.filter(p => p.id !== methodId));
      toast.success('Payment method removed');
    } catch { toast.error('Failed to remove payment method'); }
  };

  const toggleUnitInvoices = async (truckId) => {
    if (expandedUnit === truckId) { setExpandedUnit(null); return; }
    setExpandedUnit(truckId);
    if (!unitInvoices[truckId]) {
      try {
        const res = await api.get(`/invoice-transformer/customer-invoices?truck_id=${truckId}&limit=20`);
        setUnitInvoices(prev => ({ ...prev, [truckId]: res.data.items || [] }));
      } catch { setUnitInvoices(prev => ({ ...prev, [truckId]: [] })); }
    }
  };



  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin" size={28} style={{ color: 'var(--primary)' }} /></div>;
  if (!customer) return <div className="p-6" style={{ color: 'var(--text-muted)' }}>Customer not found</div>;

  const stateIdx = STATES.indexOf(customer.onboarding_state);
  const truckCount = trucks.filter(t => t.unit_type === 'Truck' || !t.unit_type).length;
  const trailerCount = trucks.filter(t => t.unit_type === 'Trailer').length;
  const activeRepairs = repairs.filter(r => !['completed', 'cancelled'].includes(r.status));

  return (
    <div className="space-y-6 animate-fade-in max-w-[70%] mx-auto" data-testid="customer-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4 pb-4 border-b" style={{ borderColor: 'var(--border)' }}>
        <button onClick={() => navigate('/ops/customers')} className="transition-colors" style={{ color: 'var(--text-muted)' }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--primary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--text-faint)' }}>// CUSTOMER PROFILE</p>
          <div className="flex items-center gap-3">
            <h1 className="font-['Manrope'] font-extrabold text-2xl" style={{ color: 'var(--text-primary)' }}>{customer.company_name}</h1>
            {/* Tenant Badge */}
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${TENANT_BADGES[customer.tenant_group]?.color || 'text-gray-400 bg-gray-900/20 border-gray-700/50'}`}
                data-testid="customer-tenant-badge">
                <Tag size={10} />
                {customer.tenant_group || 'N/A'}
              </span>
              {currentUser?.role === 'master_admin' && (
                <button onClick={() => setShowTenantModal(true)} 
                  className="p-1 rounded hover:bg-white/10 transition-colors"
                  style={{ color: 'var(--text-faint)' }}
                  title="Change tenant"
                  data-testid="change-tenant-btn">
                  <Shield size={14} />
                </button>
              )}
            </div>
          </div>
          <p className="font-['IBM_Plex_Sans'] text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {customer.contact_email}{customer.contact_phone ? ` · ${customer.contact_phone}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowAppModal(true); loadApplication(); }}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-['IBM_Plex_Sans'] rounded-md border transition-colors"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
            data-testid="view-application-btn"
          >
            <FileText size={14} /> View Full Application
          </button>
          <span className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wide border font-['IBM_Plex_Sans'] ${STATE_COLORS[customer.onboarding_state] || STATE_COLORS.NEW_LEAD}`}>
            {STATE_LABELS[customer.onboarding_state] || customer.onboarding_state?.replace(/_/g, ' ')}
          </span>
        </div>
      </div>

      {/* ═══ BILLING STATUS BANNER ═══ */}
      {billingSummary && billingSummary.status !== 'COMPLETE' && (
        <div
          className="flex items-center gap-3 p-3.5 rounded-md border"
          style={{
            background: billingSummary.pending_link ? 'rgba(246, 194, 50, 0.06)' : 'rgba(251, 146, 60, 0.06)',
            borderColor: billingSummary.pending_link ? 'rgba(246, 194, 50, 0.3)' : 'rgba(251, 146, 60, 0.3)',
          }}
          data-testid="billing-status-banner"
        >
          <Banknote size={18} style={{ color: billingSummary.pending_link ? 'var(--primary)' : '#FB923C' }} className="flex-shrink-0" />
          <div className="flex-1 min-w-0">
            {billingSummary.pending_link ? (
              <>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Billing setup link sent</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  Awaiting customer submission from <span className="font-mono">{billingSummary.pending_link.email}</span>. Link expires {new Date(billingSummary.pending_link.expires_at).toLocaleDateString()}.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Billing info not on file</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  Send the customer a secure setup link or enter their bank info manually before invoicing.
                </p>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!billingSummary.pending_link && (
              <button
                onClick={sendBillingLinkFromBanner}
                disabled={sendingBillingLink}
                data-testid="banner-send-billing-link-btn"
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-md border transition-colors disabled:opacity-50"
                style={{ background: 'var(--primary)', color: '#1A120E', borderColor: 'var(--primary)' }}
              >
                {sendingBillingLink ? <Loader2 size={12} className="animate-spin" /> : 'Send Link'}
              </button>
            )}
            <button
              onClick={() => setShowAccountDetails(true)}
              data-testid="banner-open-billing-btn"
              className="px-3 py-1.5 text-xs font-['IBM_Plex_Sans'] rounded-md border transition-colors"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
            >
              Open Billing →
            </button>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Trucks', value: truckCount, icon: Truck },
          { label: 'Trailers', value: trailerCount, icon: Hash },
          { label: 'Active Repairs', value: activeRepairs.length, icon: Wrench, highlight: activeRepairs.length > 0 },
          { label: 'Fleet Size', value: customer.fleet_size || '—', icon: Building2 },
          { label: 'Approval Limit', value: `$${customer.approval_threshold?.toLocaleString() || '750'}`, icon: TrendingUp },
        ].map(({ label, value, icon: Icon, highlight }) => (
          <div key={label} className="card-slate p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>{label}</span>
              <Icon size={14} style={{ color: highlight ? 'var(--warning)' : 'var(--border)' }} />
            </div>
            <p className={`text-xl font-['JetBrains_Mono'] font-bold`} style={{ color: highlight ? 'var(--warning)' : 'var(--text-primary)' }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Main: Units + Repairs (always visible, full width) */}
      <div className="space-y-6">
          {/* ═══ UNITS (Trucks & Trailers) ═══ */}
          <div className="card-slate p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Truck size={16} style={{ color: 'var(--primary)' }} />
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// FLEET</p>
                  <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Units ({trucks.length})</p>
                </div>
              </div>
              <button onClick={() => setShowTruckModal(true)} data-testid="add-truck-btn"
                className="flex items-center gap-2 px-3 py-1.5 border rounded-md text-xs transition-colors font-['IBM_Plex_Sans']"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}>
                <Plus size={12} /> Add Unit
              </button>
            </div>

            {/* Unit Type Filter & Search */}
            <div className="flex items-center gap-4 mb-4 flex-wrap" data-testid="unit-type-filter">
              {[
                { key: 'all', label: `All (${trucks.length})` },
                { key: 'Truck', label: `Trucks (${truckCount})` },
                { key: 'Trailer', label: `Trailers (${trailerCount})` },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer select-none" data-testid={`filter-${key}`}>
                  <input
                    type="radio"
                    name="unitFilter"
                    checked={unitFilter === key}
                    onChange={() => setUnitFilter(key)}
                    className="accent-[var(--primary)]"
                  />
                  <span className="text-xs font-['IBM_Plex_Sans']" style={{ color: unitFilter === key ? 'var(--primary)' : 'var(--text-muted)' }}>{label}</span>
                </label>
              ))}
              <div className="relative ml-auto">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-faint)' }} />
                <input
                  type="text"
                  placeholder="Search unit #..."
                  value={unitSearch}
                  onChange={e => setUnitSearch(e.target.value)}
                  className="input-slate pl-8 py-1.5 text-xs w-40"
                  data-testid="unit-search-input"
                />
              </div>
            </div>

            {trucks.length === 0 ? (
              <div className="py-8 text-center rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <Truck size={28} className="mx-auto mb-2" style={{ color: 'var(--border)' }} />
                <p className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-faint)' }}>No units added yet</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                {trucks.filter(t => {
                  const typeMatch = unitFilter === 'all' ? true : unitFilter === 'Truck' ? (t.unit_type === 'Truck' || !t.unit_type) : t.unit_type === 'Trailer';
                  const searchMatch = !unitSearch || t.unit_number?.toLowerCase().includes(unitSearch.toLowerCase()) || t.vin?.toLowerCase().includes(unitSearch.toLowerCase()) || t.make?.toLowerCase().includes(unitSearch.toLowerCase());
                  return typeMatch && searchMatch;
                }).map(t => (
                  <div key={t.id} data-testid={`unit-${t.id}`}>
                    <div className="flex items-center gap-4 p-3 rounded-md transition-colors cursor-pointer"
                      style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                      onClick={() => toggleUnitInvoices(t.id)}>
                      <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'var(--surface)' }}>
                        <Truck size={16} style={{ color: t.unit_type === 'Trailer' ? 'var(--text-muted)' : 'var(--primary)' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-['JetBrains_Mono'] text-sm font-bold" style={{ color: 'var(--primary)' }}>{t.unit_number}</span>
                          <span className="text-[9px] uppercase px-1.5 py-0.5 rounded-full font-['JetBrains_Mono']" style={{ background: 'var(--surface)', color: t.unit_type === 'Trailer' ? 'var(--warning)' : 'var(--primary)', border: '1px solid var(--border)' }}>
                            {t.unit_type || 'Truck'}
                          </span>
                        </div>
                        <p className="text-sm font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>{t.year} {t.make} {t.model}</p>
                      </div>
                      <div className="text-right flex-shrink-0 flex items-center gap-3">
                        <div>
                          <p className="font-['JetBrains_Mono'] text-xs" style={{ color: 'var(--text-muted)' }}>{t.current_mileage?.toLocaleString()} mi</p>
                          <span className={`text-[9px] uppercase tracking-wide font-bold border rounded-full px-2 py-0.5 font-['IBM_Plex_Sans'] ${TRUCK_STATUS_COLORS[t.status] || TRUCK_STATUS_COLORS.active}`}>
                            {t.status?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <ChevronDown size={14} className={`transition-transform ${expandedUnit === t.id ? 'rotate-180' : ''}`} style={{ color: 'var(--text-faint)' }} />
                      </div>
                    </div>
                    {/* Expanded invoice list */}
                    {expandedUnit === t.id && (
                      <div className="ml-14 mt-1 mb-2 p-3 rounded-md" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
                        data-testid={`unit-invoices-${t.id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Receipt size={12} style={{ color: 'var(--primary)' }} />
                          <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>Invoices for {t.unit_number}</span>
                        </div>
                        {!unitInvoices[t.id] ? (
                          <div className="flex justify-center py-3"><Loader2 size={14} className="animate-spin" style={{ color: 'var(--primary)' }} /></div>
                        ) : unitInvoices[t.id].length === 0 ? (
                          <p className="text-xs font-['IBM_Plex_Sans'] py-2" style={{ color: 'var(--text-faint)' }}>No invoices for this unit</p>
                        ) : (
                          <div className="space-y-1.5">
                            {unitInvoices[t.id].map(inv => (
                              <div key={inv.id} className="flex items-center justify-between p-2 rounded-sm" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}
                                data-testid={`unit-invoice-${inv.id}`}>
                                <div>
                                  <span className="text-xs font-['JetBrains_Mono'] font-bold" style={{ color: 'var(--primary)' }}>{inv.invoice_number}</span>
                                  <span className="text-[10px] font-['IBM_Plex_Sans'] ml-2" style={{ color: 'var(--text-muted)' }}>{inv.invoice_date}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`text-[9px] font-['JetBrains_Mono'] uppercase px-1.5 py-0.5 rounded-sm border ${inv.payment_status === 'paid' ? 'text-[var(--success)] bg-[var(--success-muted)] border-[var(--success)]' : 'text-[var(--warning)] bg-[var(--warning-muted)] border-[var(--warning)]'}`}>
                                    {inv.payment_status}
                                  </span>
                                  <span className="text-sm font-['JetBrains_Mono'] font-bold" style={{ color: 'var(--text-primary)' }}>${inv.totals?.total_due?.toLocaleString()}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ═══ RECENT REPAIRS ═══ */}
          <div className="card-slate p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Wrench size={16} style={{ color: 'var(--warning)' }} />
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// REPAIRS</p>
                  <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Recent Repairs</p>
                </div>
              </div>
              <button onClick={() => navigate(`/ops/repairs?customer_id=${id}`)} className="text-xs hover:underline font-['IBM_Plex_Sans']" style={{ color: 'var(--primary)' }}>View all →</button>
            </div>

            {repairs.length === 0 ? (
              <div className="py-8 text-center rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <Wrench size={28} className="mx-auto mb-2" style={{ color: 'var(--border)' }} />
                <p className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-faint)' }}>No repair history</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                {repairs.map(r => (
                  <div key={r.id} onClick={() => navigate(`/ops/repairs/${r.id}`)}
                    className="flex items-center gap-3 p-3 rounded-md cursor-pointer transition-colors"
                    style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                    data-testid={`repair-${r.id}`}>
                    {r.priority === 'emergency' && <AlertTriangle size={14} className="flex-shrink-0" style={{ color: 'var(--error)' }} />}
                    <span className="font-['JetBrains_Mono'] text-xs font-bold flex-shrink-0" style={{ color: 'var(--primary)' }}>{r.truck_unit_number}</span>
                    <span className="text-sm font-['IBM_Plex_Sans'] flex-1 truncate" style={{ color: 'var(--text-primary)' }}>{r.issue_description}</span>
                    <span className={`flex-shrink-0 inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${REPAIR_STATUS_COLORS[r.status] || REPAIR_STATUS_COLORS.open}`}>
                      {formatRepairStatus(r.status)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
      </div>

      {/* ═══ ACCOUNT DETAILS TOGGLE ═══ */}
      <button
        onClick={() => setShowAccountDetails(s => !s)}
        data-testid="toggle-account-details-btn"
        className="w-full flex items-center justify-between px-5 py-3.5 rounded-md border transition-colors font-['IBM_Plex_Sans'] hover:border-[var(--primary)]"
        style={{ background: 'var(--bg)', borderColor: 'var(--border)', color: 'var(--text-primary)' }}
      >
        <div className="flex items-center gap-3">
          <FileText size={15} style={{ color: 'var(--text-muted)' }} />
          <div className="text-left">
            <p className="text-sm font-semibold">Account Details</p>
            <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-faint)' }}>Health · Tier · Onboarding · Tracking · Addresses · Billing · Notes</p>
          </div>
        </div>
        {showAccountDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {showAccountDetails && (
        <div className="space-y-6">
          {/* ═══ FLEET HEALTH SCORE ═══ */}
          <div className="card-slate p-5">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp size={16} style={{ color: healthScore?.color === 'green' ? 'var(--success)' : healthScore?.color === 'yellow' ? 'var(--warning)' : healthScore?.color === 'red' ? 'var(--error)' : 'var(--primary)' }} />
              <div>
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// HEALTH</p>
                <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Fleet Health Score</p>
              </div>
            </div>

            {scoreLoading ? (
              <div className="py-8 text-center"><Loader2 className="animate-spin mx-auto" size={20} style={{ color: 'var(--primary)' }} /></div>
            ) : healthScore && healthScore.score != null ? (
              <div className="text-center">
                <p className={`text-5xl font-['JetBrains_Mono'] font-bold mb-2 ${
                  healthScore.color === 'green' ? 'text-[var(--success)]' :
                  healthScore.color === 'teal' ? 'text-teal-400' :
                  healthScore.color === 'yellow' ? 'text-[var(--warning)]' :
                  healthScore.color === 'orange' ? 'text-orange-400' : 'text-[var(--error)]'
                }`}>{healthScore.score}</p>
                <p className="font-['Manrope'] font-bold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>{healthScore.label}</p>
                <p className="text-xs font-['IBM_Plex_Sans'] mb-4" style={{ color: 'var(--text-faint)' }}>Based on last 90 days</p>
                
                {/* Score breakdown */}
                {healthScore.breakdown && (
                  <div className="grid grid-cols-2 gap-2 mb-4 text-left">
                    {Object.entries(healthScore.breakdown).slice(0, 4).map(([key, val]) => {
                      // Handle nested objects like {score: 17, max: 25, label: "Ratio", detail: "..."}
                      let displayValue = val;
                      if (typeof val === 'object' && val !== null) {
                        if (val.score !== undefined) {
                          displayValue = `${val.score}/${val.max || 25}`;
                        } else {
                          displayValue = val.label || val.value || '—';
                        }
                      }
                      return (
                        <div key={key} className="p-2 rounded-md" style={{ background: 'var(--bg)' }}>
                          <p className="text-[9px] font-['JetBrains_Mono'] uppercase" style={{ color: 'var(--text-faint)' }}>{key.replace(/_/g, ' ')}</p>
                          <p className="text-sm font-['JetBrains_Mono'] font-bold" style={{ color: 'var(--text-primary)' }}>
                            {String(displayValue)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                <button onClick={() => setShowShareModal(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md text-xs font-['IBM_Plex_Sans'] transition-colors"
                  style={{ background: 'var(--primary-muted)', border: '1px solid var(--primary)', color: 'var(--primary)' }}>
                  <Send size={12} /> Share Report with Client
                </button>
              </div>
            ) : (
              <div className="py-8 text-center rounded-md" style={{ background: 'var(--bg)' }}>
                <TrendingUp size={28} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
                <p className="font-['Manrope'] font-bold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>No Data Yet</p>
                <p className="text-xs font-['IBM_Plex_Sans'] mb-1" style={{ color: 'var(--text-faint)' }}>
                  {healthScore?.message || 'Score will appear once this customer has any repair, PM, or DOT activity.'}
                </p>
                <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>
                  {healthScore?.truck_count || 0} units · 0 data points
                </p>
              </div>
            )}
          </div>

          {/* ═══ PRICING TIER ═══ */}
          <div className="card-slate p-5" data-testid="pricing-tier-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <DollarSign size={16} style={{ color: 'var(--warning)' }} />
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// BILLING</p>
                  <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Pricing Tier</p>
                </div>
              </div>
              {['master_admin', 'admin'].includes(currentUser?.role) && (
                <button onClick={() => { setSelectedTierId(customer?.pricing_tier_id || ''); setShowTierModal(true); }}
                  className="px-3 py-1.5 rounded-md text-xs font-bold font-['Manrope'] uppercase tracking-wide transition-colors"
                  style={{ background: 'var(--warning)', color: 'white' }}
                  data-testid="change-tier-btn">
                  Change
                </button>
              )}
            </div>

            {customerTier ? (
              <div>
                <div className="p-4 rounded-md text-center mb-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <p className="text-3xl font-['JetBrains_Mono'] font-bold mb-1" style={{ color: 'var(--warning)' }}>{customerTier.markup_percent}%</p>
                  <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{customerTier.name}</p>
                  <p className="text-xs font-['IBM_Plex_Sans'] mt-1" style={{ color: 'var(--text-faint)' }}>{customerTier.description}</p>
                </div>

                {/* Example calculation */}
                <div className="p-3 rounded-md mb-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>Example</p>
                  <div className="flex items-center justify-between text-xs font-['IBM_Plex_Sans']">
                    <span style={{ color: 'var(--text-muted)' }}>$100.00 base</span>
                    <span style={{ color: 'var(--text-faint)' }}>+{customerTier.markup_percent}%</span>
                    <span className="font-bold font-['JetBrains_Mono']" style={{ color: 'var(--success)' }}>
                      ${(100 * (1 + customerTier.markup_percent / 100)).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Tier change history */}
                {tierHistory.length > 0 && (
                  <div className="pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                    <div className="flex items-center gap-1.5 mb-2">
                      <History size={12} style={{ color: 'var(--text-faint)' }} />
                      <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>Change History</p>
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {[...tierHistory].reverse().slice(0, 5).map((h, i) => (
                        <div key={i} className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>
                          <span style={{ color: 'var(--text-faint)' }}>{h.changed_at ? new Date(h.changed_at).toLocaleDateString() : '—'}</span>
                          {' — '}{h.from_tier_name || 'None'} → <span style={{ color: 'var(--warning)' }}>{h.to_tier_name}</span>
                          <span className="text-[10px]" style={{ color: 'var(--text-faint)' }}> by {h.changed_by_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-6 text-center rounded-md" style={{ background: 'var(--bg)' }}>
                <DollarSign size={24} className="mx-auto mb-2" style={{ color: 'var(--border)' }} />
                <p className="text-sm font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>No tier assigned</p>
              </div>
            )}
          </div>

          {/* ═══ RECURRING BILLING CARD ═══ */}
          <div className="card-slate p-5" data-testid="recurring-billing-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <DollarSign size={16} style={{ color: customer.billing_mode === 'MONTHLY' ? '#60A5FA' : customer.billing_mode === 'PER_UNIT' ? '#10B981' : 'var(--text-muted)' }} />
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// BILLING</p>
                  <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Recurring Fee</p>
                </div>
              </div>
              {['master_admin', 'admin'].includes(currentUser?.role) && (
                <button onClick={() => {
                  setBillingForm({
                    billing_mode: customer.billing_mode || '',
                    billing_rate: customer.billing_rate ?? '',
                    billing_units_limit: customer.billing_units_limit ?? '',
                  });
                  setShowBillingModal(true);
                }}
                  className="px-3 py-1.5 rounded-md text-xs font-bold font-['Manrope'] uppercase tracking-wide transition-colors"
                  style={{ background: 'var(--primary)', color: 'white' }}
                  data-testid="change-billing-btn">
                  {customer.billing_mode ? 'Edit' : 'Setup'}
                </button>
              )}
            </div>

            {customer.billing_mode === 'MONTHLY' ? (
              <div className="p-4 rounded-md text-center" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <p className="text-3xl font-['JetBrains_Mono'] font-bold mb-1" style={{ color: '#60A5FA' }}>${Number(customer.billing_rate || 0).toFixed(2)}</p>
                <p className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>flat per month</p>
              </div>
            ) : customer.billing_mode === 'PER_UNIT' ? (
              <div className="p-4 rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <div className="text-center mb-3">
                  <p className="text-3xl font-['JetBrains_Mono'] font-bold mb-1" style={{ color: '#10B981' }}>${Number(customer.billing_rate || 0).toFixed(2)}</p>
                  <p className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>per truck per month</p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs font-['IBM_Plex_Sans']">
                  <div className="p-2 rounded text-center" style={{ background: 'var(--surface)' }}>
                    <p style={{ color: 'var(--text-faint)' }}>Current trucks</p>
                    <p className="font-bold text-sm font-['JetBrains_Mono']" style={{ color: 'var(--text-primary)' }}>{customer.fleet_size || 0}</p>
                  </div>
                  <div className="p-2 rounded text-center" style={{ background: 'var(--surface)' }}>
                    <p style={{ color: 'var(--text-faint)' }}>Max allowed</p>
                    <p className="font-bold text-sm font-['JetBrains_Mono']" style={{ color: customer.billing_units_limit && customer.fleet_size >= customer.billing_units_limit ? 'var(--error)' : 'var(--text-primary)' }}>
                      {customer.billing_units_limit ?? '∞'}
                    </p>
                  </div>
                </div>
                {customer.billing_units_limit && customer.fleet_size >= customer.billing_units_limit && (
                  <p className="text-xs mt-2 font-['IBM_Plex_Sans'] text-center" style={{ color: 'var(--error)' }}>
                    ⚠ Truck limit reached — new trucks will be blocked until you raise the cap
                  </p>
                )}
                <p className="text-xs mt-2 font-['JetBrains_Mono'] text-center" style={{ color: 'var(--text-faint)' }}>
                  Projected: ${(Number(customer.billing_rate || 0) * Number(customer.fleet_size || 0)).toFixed(2)}/mo
                </p>
              </div>
            ) : (
              <div className="p-4 rounded-md text-center" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <p className="text-sm font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>No recurring billing</p>
                <p className="text-xs mt-1 font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>Markup-only customer</p>
              </div>
            )}
          </div>

          {/* ═══ ONBOARDING JOURNEY (compact for graduated customers) ═══ */}
          {customer.onboarding_state === 'CONTRACT_SIGNED' ? (
            <div className="card-slate p-4" data-testid="onboarded-badge">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--success-muted)', border: '1px solid var(--success)' }}>
                    <CheckCircle2 size={14} style={{ color: 'var(--success)' }} />
                  </div>
                  <div>
                    <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// ONBOARDING</p>
                    <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--success)' }}>
                      Onboarded
                      {customer.onboarded_at && (
                        <span className="ml-2 text-xs font-normal font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>
                          · {new Date(customer.onboarded_at).toLocaleDateString()}
                        </span>
                      )}
                      {customer.sales_rep_name && (
                        <span className="ml-1 text-xs font-normal font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>
                          · by {customer.sales_rep_name}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowJourneyModal(true)}
                    className="px-3 py-1.5 rounded-md text-xs font-['IBM_Plex_Sans'] transition-colors border"
                    style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                    data-testid="view-journey-btn">
                    View Journey →
                  </button>
                  {currentUser?.role === 'master_admin' && (
                    <button onClick={() => { setNewState('NEW_LEAD'); setShowStateModal(true); }}
                      className="px-2 py-1.5 rounded-md text-[10px] font-['IBM_Plex_Sans'] transition-colors border"
                      style={{ borderColor: 'var(--border)', color: 'var(--text-faint)' }}
                      title="Re-onboard (master admin only)"
                      data-testid="reonboard-btn">
                      Re-onboard
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
          <div className="card-slate p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Calendar size={16} style={{ color: 'var(--primary)' }} />
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// ONBOARDING</p>
                  <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Pipeline</p>
                </div>
              </div>
              <button onClick={() => { setNewState(STATES[Math.min(stateIdx + 1, STATES.length - 1)]); setShowStateModal(true); }}
                className="px-3 py-1.5 rounded-md text-xs font-bold font-['Manrope'] uppercase tracking-wide transition-colors"
                style={{ background: 'var(--primary)', color: 'white' }}
                data-testid="update-state-btn">
                Update
              </button>
            </div>

            {/* Visual pipeline */}
            <div className="space-y-1 mb-4">
              {STATES.map((s, i) => (
                <div key={s} className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-['IBM_Plex_Sans'] transition-all ${
                  i === stateIdx ? 'font-bold' : ''
                }`} style={{
                  background: i === stateIdx ? 'var(--primary-muted)' : i < stateIdx ? 'var(--success-muted)' : 'transparent',
                  color: i === stateIdx ? 'var(--primary)' : i < stateIdx ? 'var(--success)' : 'var(--text-faint)',
                  border: `1px solid ${i === stateIdx ? 'var(--primary)' : i < stateIdx ? 'var(--success)' : 'var(--border)'}`
                }}>
                  <div className={`w-2 h-2 rounded-full ${i <= stateIdx ? '' : 'opacity-30'}`} style={{ background: i === stateIdx ? 'var(--primary)' : i < stateIdx ? 'var(--success)' : 'var(--text-faint)' }} />
                  {STATE_LABELS[s] || s.replace(/_/g, ' ')}
                </div>
              ))}
            </div>

            {/* History */}
            {customer.onboarding_events?.length > 0 && (
              <div className="pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>History</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {[...customer.onboarding_events].reverse().slice(0, 5).map((ev) => (
                    <div key={ev.id} className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>
                      <span style={{ color: 'var(--text-faint)' }}>{new Date(ev.timestamp).toLocaleDateString()}</span> — {ev.from_state} → <span style={{ color: 'var(--primary)' }}>{ev.to_state}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}

          {/* ═══ TRACKING SETTINGS ═══ */}
          <div className="card-slate p-5">
            <div className="flex items-center gap-3 mb-4">
              <Bell size={16} style={{ color: 'var(--warning)' }} />
              <div>
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// TRACKING</p>
                <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Alert Settings</p>
              </div>
            </div>

            <p className="text-xs font-['IBM_Plex_Sans'] mb-4" style={{ color: 'var(--text-muted)' }}>
              Alert ops when same part/category was recently repaired within this window.
            </p>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[9px] font-['JetBrains_Mono'] uppercase mb-1" style={{ color: 'var(--text-faint)' }}>Miles</label>
                  <input type="number" min="500" max="100000" step="500" value={alertSettings.default_miles}
                    onChange={e => setAlertSettings(p => ({ ...p, default_miles: e.target.value }))}
                    className="w-full input-slate text-center"
                    data-testid="default-miles-input" />
                </div>
                <div>
                  <label className="block text-[9px] font-['JetBrains_Mono'] uppercase mb-1" style={{ color: 'var(--text-faint)' }}>Days</label>
                  <input type="number" min="1" max="3650" value={alertSettings.default_days}
                    onChange={e => setAlertSettings(p => ({ ...p, default_days: e.target.value }))}
                    className="w-full input-slate text-center"
                    data-testid="default-days-input" />
                </div>
              </div>

              {/* Category overrides */}
              {Object.keys(alertSettings.category_overrides || {}).length > 0 && (
                <div className="space-y-2">
                  {Object.entries(alertSettings.category_overrides).map(([cat, vals]) => {
                    const catLabel = CATEGORY_LIST.find(c => c.value === cat)?.label || cat;
                    return (
                      <div key={cat} className="flex items-center gap-2 p-2 rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                        <span className="font-['JetBrains_Mono'] text-[10px] flex-1 truncate" style={{ color: 'var(--primary)' }}>{catLabel}</span>
                        <input type="number" min="500" max="100000" step="500" value={vals.miles}
                          onChange={e => setAlertSettings(p => ({ ...p, category_overrides: { ...p.category_overrides, [cat]: { ...vals, miles: e.target.value } } }))}
                          className="w-16 h-7 px-2 rounded-md text-xs font-['JetBrains_Mono'] text-center"
                          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} />
                        <input type="number" min="1" max="3650" value={vals.days}
                          onChange={e => setAlertSettings(p => ({ ...p, category_overrides: { ...p.category_overrides, [cat]: { ...vals, days: e.target.value } } }))}
                          className="w-14 h-7 px-2 rounded-md text-xs font-['JetBrains_Mono'] text-center"
                          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} />
                        <button onClick={() => removeCategoryOverride(cat)} style={{ color: 'var(--text-faint)' }}
                          onMouseEnter={e => e.currentTarget.style.color = 'var(--error)'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-faint)'}>
                          <X size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex items-center gap-2">
                <select value={newOverrideCat} onChange={e => setNewOverrideCat(e.target.value)} className="flex-1 select-slate text-xs">
                  <option value="">Add override...</option>
                  {CATEGORY_LIST.filter(c => !alertSettings.category_overrides?.[c.value]).map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                <button onClick={addCategoryOverride} disabled={!newOverrideCat}
                  className="px-3 py-2 rounded-md text-xs font-['JetBrains_Mono'] transition-colors disabled:opacity-40"
                  style={{ border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                  + Add
                </button>
              </div>

              <button onClick={saveAlertSettings} disabled={savingAlerts}
                className="w-full btn-primary flex items-center justify-center gap-2 text-xs uppercase tracking-wide"
                data-testid="save-alert-settings-btn">
                {savingAlerts && <Loader2 size={12} className="animate-spin" />}
                Save Settings
              </button>
            </div>
          </div>

      {/* ═══ ADDRESSES ═══ */}
      <div className="card-slate p-5" data-testid="addresses-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MapPin size={16} style={{ color: 'var(--text-muted)' }} />
            <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Addresses</p>
          </div>
          <button onClick={openAddressEditor}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded-sm border transition-colors font-['IBM_Plex_Sans']"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
            data-testid="edit-addresses-btn">
            <Pencil size={12} /> Edit
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>Physical Address</p>
            <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
              {customer.address || <span style={{ color: 'var(--text-faint)' }}>Not set</span>}
            </p>
            {(customer.city || customer.state) && (
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {[customer.city, customer.state, customer.zip_code].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide mb-1 flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
              <Mail size={10} /> Mailing / Bill To
            </p>
            {customer.mailing_same_as_physical !== false ? (
              <p className="text-xs italic" style={{ color: 'var(--text-faint)' }}>Same as physical</p>
            ) : (
              <>
                <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                  {customer.mailing_street_address || <span style={{ color: 'var(--text-faint)' }}>Not set</span>}
                </p>
                {(customer.mailing_city || customer.mailing_state) && (
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {[customer.mailing_city, customer.mailing_state, customer.mailing_zip_code].filter(Boolean).join(', ')}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
        {(customer.additional_addresses || []).length > 0 && (
          <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
            <p className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Additional Addresses</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {customer.additional_addresses.map((a, i) => (
                <div key={i} className="text-sm">
                  <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{a.label || `Address #${i + 1}`}</p>
                  <p style={{ color: 'var(--text-muted)' }}>{a.street_address}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {[a.city, a.state, a.zip_code].filter(Boolean).join(', ')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ═══ BILLING INFORMATION (ACH + optional card) ═══ */}
      <BillingInfoCard customerId={id} currentUser={currentUser} />

      {/* ═══ CUSTOMER LOGIN (set / reset password) ═══ */}
      <CustomerLoginCard customerId={id} currentUser={currentUser} />

      {/* ═══ INTERNAL NOTES (Ops Only) ═══ */}
      <div className="card-slate p-4 space-y-3" data-testid="ops-notes-section">
        <div className="flex items-center justify-between">
          <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Internal Notes (Ops Only)</p>
          <span className="text-[9px] font-['JetBrains_Mono'] px-2 py-0.5 rounded-full" style={{ background: 'rgba(59,130,246,0.1)', color: '#3B82F6', border: '1px solid rgba(59,130,246,0.3)' }}>NOT VISIBLE TO CUSTOMER</span>
        </div>
        <textarea
          value={opsNotes}
          onChange={e => setOpsNotes(e.target.value)}
          rows={4}
          className="w-full input-slate resize-none text-sm font-['IBM_Plex_Sans']"
          placeholder="Add internal notes visible only to ops team..."
          data-testid="ops-notes-input"
        />
        <button onClick={handleSaveOpsNotes} disabled={savingOpsNotes}
          className="btn-primary flex items-center justify-center gap-2 text-xs uppercase tracking-wide px-4 py-1.5"
          data-testid="save-ops-notes-btn">
          {savingOpsNotes && <Loader2 size={12} className="animate-spin" />}
          Save Notes
        </button>
      </div>

        </div>
      )}

      {/* ═══ MODALS ═══ */}
      <StateChangeModal open={showStateModal} onOpenChange={setShowStateModal}
        newState={newState} setNewState={setNewState} stateNote={stateNote} setStateNote={setStateNote}
        saving={saving} onSubmit={handleStateChange} />

      <PaymentModal open={showPaymentModal} onOpenChange={setShowPaymentModal}
        paymentForm={paymentForm} setPaymentForm={setPaymentForm}
        savingPayment={savingPayment} onSubmit={handleAddPayment} />

      <AddUnitModal open={showTruckModal} onOpenChange={setShowTruckModal}
        truckForm={truckForm} setTruckForm={setTruckForm}
        saving={saving} onSubmit={handleAddTruck} />

      <ShareHealthModal open={showShareModal} onOpenChange={setShowShareModal}
        healthScore={healthScore} customer={customer}
        shareMsg={shareMsg} setShareMsg={setShareMsg}
        sharing={sharing} onSubmit={handleShareScore} />

      <TenantModal open={showTenantModal} onOpenChange={setShowTenantModal}
        customer={customer} tenantChanging={tenantChanging}
        tenantReason={tenantReason} setTenantReason={setTenantReason}
        onChangeTenant={handleChangeTenant} />

      <TierChangeModal open={showTierModal} onOpenChange={setShowTierModal}
        customer={customer} customerTier={customerTier} pricingTiers={pricingTiers}
        selectedTierId={selectedTierId} setSelectedTierId={setSelectedTierId}
        tierConfirmText={tierConfirmText} setTierConfirmText={setTierConfirmText}
        tierChanging={tierChanging} onSubmit={handleChangeTier} />

      {/* Billing config edit modal */}
      <Dialog open={showBillingModal} onOpenChange={setShowBillingModal}>
        <DialogContent className="max-w-md" data-testid="billing-edit-modal">
          <DialogHeader>
            <DialogTitle>Recurring Billing Configuration</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            setSavingBilling(true);
            try {
              const body = billingForm.billing_mode ? {
                billing_mode: billingForm.billing_mode,
                billing_rate: Number(billingForm.billing_rate),
                billing_units_limit: billingForm.billing_mode === 'PER_UNIT' && billingForm.billing_units_limit !== '' ? Number(billingForm.billing_units_limit) : null,
              } : { billing_mode: null, billing_rate: null };
              await api.patch(`/customers/${customer.id}/billing-config`, body);
              toast.success('Billing updated');
              setShowBillingModal(false);
              load();
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Failed to update billing');
            } finally {
              setSavingBilling(false);
            }
          }} className="space-y-4 mt-2">
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: '', label: 'None', col: 'slate' },
                { v: 'MONTHLY', label: 'Monthly Flat', col: 'blue' },
                { v: 'PER_UNIT', label: 'Per Truck', col: 'emerald' },
              ].map(opt => (
                <label key={opt.v} className={`flex items-center justify-center p-2 rounded-md cursor-pointer border-2 transition-colors text-xs ${billingForm.billing_mode === opt.v ? `border-${opt.col}-500 bg-${opt.col}-900/20` : 'border-[var(--border)]'}`}>
                  <input type="radio" name="billing_mode" value={opt.v} checked={billingForm.billing_mode === opt.v}
                    onChange={() => setBillingForm(p => ({ ...p, billing_mode: opt.v }))} className="sr-only" />
                  <span className="font-['IBM_Plex_Sans'] font-bold" style={{ color: billingForm.billing_mode === opt.v ? 'var(--text-primary)' : 'var(--text-muted)' }}>{opt.label}</span>
                </label>
              ))}
            </div>
            {billingForm.billing_mode && (
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                  {billingForm.billing_mode === 'MONTHLY' ? 'Monthly Fee ($)' : 'Per Truck Fee ($)'}
                </label>
                <input type="number" min="0" step="0.01" required value={billingForm.billing_rate}
                  onChange={e => setBillingForm(p => ({ ...p, billing_rate: e.target.value }))}
                  placeholder={billingForm.billing_mode === 'MONTHLY' ? '99.00' : '5.00'}
                  className="input-slate w-full" data-testid="billing-modal-rate" />
              </div>
            )}
            {billingForm.billing_mode === 'PER_UNIT' && (
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                  Max Trucks Allowed (cap)
                </label>
                <input type="number" min="0" step="1" value={billingForm.billing_units_limit}
                  onChange={e => setBillingForm(p => ({ ...p, billing_units_limit: e.target.value }))}
                  placeholder="(no cap)"
                  className="input-slate w-full" data-testid="billing-modal-limit" />
                <p className="text-xs mt-1" style={{ color: 'var(--text-faint)' }}>
                  Blocks new trucks once reached. Leave empty for unlimited.
                </p>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setShowBillingModal(false)} className="flex-1 px-4 py-2 rounded-md text-sm font-bold border" style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>Cancel</button>
              <button type="submit" disabled={savingBilling} className="flex-1 px-4 py-2 rounded-md text-sm font-bold" style={{ background: 'var(--primary)', color: 'white', opacity: savingBilling ? 0.5 : 1 }} data-testid="billing-modal-save">
                {savingBilling ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Address Editor Modal */}
      <Dialog open={showAddrModal} onOpenChange={setShowAddrModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="address-edit-modal">
          <DialogHeader>
            <DialogTitle>Edit Addresses</DialogTitle>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Update physical, mailing, and additional addresses. Mailing address is used for "Bill To" on invoices.
            </p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Physical */}
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Physical Address</p>
              <input className="input-slate w-full text-sm" placeholder="Street Address"
                value={addrForm.address} onChange={e => setAddrForm({ ...addrForm, address: e.target.value })} />
              <div className="grid grid-cols-3 gap-2">
                <input className="input-slate text-sm" placeholder="City"
                  value={addrForm.city} onChange={e => setAddrForm({ ...addrForm, city: e.target.value })} />
                <input className="input-slate text-sm" placeholder="State"
                  value={addrForm.state} onChange={e => setAddrForm({ ...addrForm, state: e.target.value })} />
                <input className="input-slate text-sm" placeholder="Zip"
                  value={addrForm.zip_code} onChange={e => setAddrForm({ ...addrForm, zip_code: e.target.value })} />
              </div>
            </div>

            {/* Mailing */}
            <div className="rounded-lg p-3" style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.2)' }}>
              <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--text-primary)' }}>
                <input type="checkbox" checked={addrForm.mailing_same_as_physical}
                  onChange={e => setAddrForm({ ...addrForm, mailing_same_as_physical: e.target.checked })} />
                Mailing address is the same as physical
              </label>
              {!addrForm.mailing_same_as_physical && (
                <div className="mt-3 space-y-2">
                  <input className="input-slate w-full text-sm" placeholder="Mailing Street Address"
                    value={addrForm.mailing_street_address} onChange={e => setAddrForm({ ...addrForm, mailing_street_address: e.target.value })} />
                  <div className="grid grid-cols-3 gap-2">
                    <input className="input-slate text-sm" placeholder="City"
                      value={addrForm.mailing_city} onChange={e => setAddrForm({ ...addrForm, mailing_city: e.target.value })} />
                    <input className="input-slate text-sm" placeholder="State"
                      value={addrForm.mailing_state} onChange={e => setAddrForm({ ...addrForm, mailing_state: e.target.value })} />
                    <input className="input-slate text-sm" placeholder="Zip"
                      value={addrForm.mailing_zip_code} onChange={e => setAddrForm({ ...addrForm, mailing_zip_code: e.target.value })} />
                  </div>
                </div>
              )}
            </div>

            {/* Additional addresses */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Additional Addresses</p>
                <button type="button" onClick={() => setAddrForm({ ...addrForm, additional_addresses: [...addrForm.additional_addresses, { label: '', street_address: '', city: '', state: '', zip_code: '' }] })}
                  className="text-xs px-2 py-1 rounded" style={{ background: 'var(--primary)', color: 'white' }}>
                  + Add
                </button>
              </div>
              {addrForm.additional_addresses.map((a, idx) => (
                <div key={idx} className="rounded p-3 mb-2 space-y-2" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <div className="flex items-center gap-2">
                    <input className="input-slate flex-1 text-sm" placeholder="Label (e.g. Terminal, Garage)"
                      value={a.label}
                      onChange={e => {
                        const next = [...addrForm.additional_addresses];
                        next[idx] = { ...next[idx], label: e.target.value };
                        setAddrForm({ ...addrForm, additional_addresses: next });
                      }} />
                    <button type="button" className="text-xs text-red-400 hover:text-red-300 px-2"
                      onClick={() => setAddrForm({ ...addrForm, additional_addresses: addrForm.additional_addresses.filter((_, i) => i !== idx) })}>
                      Remove
                    </button>
                  </div>
                  <input className="input-slate w-full text-sm" placeholder="Street Address"
                    value={a.street_address}
                    onChange={e => {
                      const next = [...addrForm.additional_addresses];
                      next[idx] = { ...next[idx], street_address: e.target.value };
                      setAddrForm({ ...addrForm, additional_addresses: next });
                    }} />
                  <div className="grid grid-cols-3 gap-2">
                    <input className="input-slate text-sm" placeholder="City" value={a.city}
                      onChange={e => { const n = [...addrForm.additional_addresses]; n[idx] = { ...n[idx], city: e.target.value }; setAddrForm({ ...addrForm, additional_addresses: n }); }} />
                    <input className="input-slate text-sm" placeholder="State" value={a.state}
                      onChange={e => { const n = [...addrForm.additional_addresses]; n[idx] = { ...n[idx], state: e.target.value }; setAddrForm({ ...addrForm, additional_addresses: n }); }} />
                    <input className="input-slate text-sm" placeholder="Zip" value={a.zip_code}
                      onChange={e => { const n = [...addrForm.additional_addresses]; n[idx] = { ...n[idx], zip_code: e.target.value }; setAddrForm({ ...addrForm, additional_addresses: n }); }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddrModal(false)} disabled={savingAddr}>Cancel</Button>
            <Button onClick={saveAddresses} disabled={savingAddr} data-testid="save-addresses-btn">
              {savingAddr ? 'Saving…' : 'Save Addresses'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Application Modal */}
      <Dialog open={showAppModal} onOpenChange={setShowAppModal}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="full-application-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} /> Full Application — {customer.company_name}
            </DialogTitle>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Read-only view of the original intake submission and payment methods on file.
            </p>
          </DialogHeader>
          {loadingApp ? (
            <div className="py-12 flex items-center justify-center"><Loader2 className="animate-spin" /></div>
          ) : !appData?.intake ? (
            <div className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
              No intake application is linked to this customer.
              {(appData?.payment_methods?.length > 0) && (
                <PaymentMethodsBlock methods={appData.payment_methods} />
              )}
            </div>
          ) : (
            <ApplicationView appData={appData} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAppModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onboarding Journey Modal — historical view for graduated customers */}
      <Dialog open={showJourneyModal} onOpenChange={setShowJourneyModal}>
        <DialogContent className="max-w-2xl" data-testid="journey-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar size={18} /> Onboarding Journey — {customer.company_name}
            </DialogTitle>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              How this customer became a customer.
            </p>
          </DialogHeader>

          <div className="py-2 space-y-4">
            {/* Stage progression — all green for graduated */}
            <div>
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>Stages Completed</p>
              <div className="space-y-1">
                {STATES.map((s) => (
                  <div key={s} className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-['IBM_Plex_Sans']"
                    style={{ background: 'var(--success-muted)', color: 'var(--success)', border: '1px solid var(--success)' }}>
                    <CheckCircle2 size={11} />
                    {STATE_LABELS[s] || s.replace(/_/g, ' ')}
                  </div>
                ))}
              </div>
            </div>

            {/* Full event timeline */}
            {customer.onboarding_events?.length > 0 && (
              <div>
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>Event History</p>
                <div className="space-y-1.5 max-h-64 overflow-y-auto pr-2">
                  {[...customer.onboarding_events].reverse().map((ev) => (
                    <div key={ev.id} className="text-xs font-['IBM_Plex_Sans'] p-2 rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                      <div className="flex items-center justify-between">
                        <span>
                          {STATE_LABELS[ev.from_state] || ev.from_state} → <span style={{ color: 'var(--primary)' }}>{STATE_LABELS[ev.to_state] || ev.to_state}</span>
                        </span>
                        <span style={{ color: 'var(--text-faint)' }}>{new Date(ev.timestamp).toLocaleString()}</span>
                      </div>
                      {ev.note && <p className="mt-1" style={{ color: 'var(--text-faint)' }}>{ev.note}</p>}
                      {ev.changed_by_email && <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-faint)' }}>by {ev.changed_by_email}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sales credit */}
            {customer.sales_rep_name && (
              <div className="pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1" style={{ color: 'var(--text-faint)' }}>Sales Credit</p>
                <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{customer.sales_rep_name}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJourneyModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Sub-components for Full Application view ───
function Field({ label, value }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color: 'var(--text-muted)' }}>{label}</p>
      <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{String(value)}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg p-4" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
      <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--primary)' }}>{title}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function PaymentMethodsBlock({ methods }) {
  if (!methods?.length) {
    return <p className="text-xs italic" style={{ color: 'var(--text-faint)' }}>No payment methods on file.</p>;
  }
  return (
    <div className="space-y-2">
      {methods.map((m, i) => (
        <div key={i} className="flex items-center justify-between rounded-md p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div className="flex items-center gap-3">
            <CreditCard size={16} style={{ color: 'var(--text-muted)' }} />
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                {m.method_type === 'ACH' ? 'ACH / Bank' : m.method_type || 'Card'}
                {m.last4 ? ` •••• ${m.last4}` : ''}
              </p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {m.bank_name || m.brand || ''} {m.is_default ? '· Default' : ''}
              </p>
            </div>
          </div>
          {m.created_at && (
            <span className="text-xs" style={{ color: 'var(--text-faint)' }}>
              Added {new Date(m.created_at).toLocaleDateString()}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function ApplicationView({ appData }) {
  const fd = appData.intake.form_data || {};
  const biz = fd.business_info || {};
  const owner = fd.principal_owner || {};
  const bank = fd.bank_reference || {};
  const refs = fd.credit_references || [];
  const tax = fd.tax_status || {};
  const sig = fd.signature || {};
  const docs = appData.intake.documents || [];

  return (
    <div className="space-y-4">
      {/* Intake meta */}
      <div className="flex flex-wrap gap-3 text-xs" style={{ color: 'var(--text-muted)' }}>
        <span><strong style={{ color: 'var(--text-primary)' }}>Source:</strong> {appData.intake.source}</span>
        {appData.intake.submitted_at && (
          <span><strong style={{ color: 'var(--text-primary)' }}>Submitted:</strong> {new Date(appData.intake.submitted_at).toLocaleString()}</span>
        )}
        {appData.intake.reviewed_at && (
          <span><strong style={{ color: 'var(--text-primary)' }}>Reviewed:</strong> {new Date(appData.intake.reviewed_at).toLocaleString()}</span>
        )}
      </div>

      <Section title="Business Information">
        <Field label="Legal Business Name" value={biz.legal_business_name} />
        <Field label="DBA / Trade Names" value={biz.trade_names_dba} />
        <Field label="Business Type" value={biz.business_type} />
        <Field label="Date of Organization" value={biz.date_of_organization} />
        <Field label="State of Organization" value={biz.state_of_organization} />
        <Field label="DOT Number" value={biz.dot_number} />
        <Field label="FEIN Number" value={biz.fein_number} />
        <Field label="Email" value={biz.email} />
        <Field label="Phone" value={biz.phone} />
        <Field label="Trucks" value={biz.num_trucks} />
        <Field label="Trailers" value={biz.num_trailers} />
        <Field label="Credit Requested" value={biz.credit_amount_requested ? `$${biz.credit_amount_requested}` : ''} />
        <Field label="PO Required" value={biz.po_required ? 'Yes' : 'No'} />
        <Field label="Person Responsible for Bills" value={biz.person_responsible_for_bills} />
        <Field label="Former Name" value={biz.former_name} />
        <Field label="Affiliate Entities" value={biz.affiliate_entities} />
      </Section>

      <Section title="Physical Address">
        <Field label="Street" value={biz.street_address} />
        <Field label="City" value={biz.city} />
        <Field label="State" value={biz.state} />
        <Field label="Zip" value={biz.zip_code} />
      </Section>

      {!biz.mailing_same_as_physical && (
        <Section title="Mailing Address (Bill To)">
          <Field label="Street" value={biz.mailing_street_address} />
          <Field label="City" value={biz.mailing_city} />
          <Field label="State" value={biz.mailing_state} />
          <Field label="Zip" value={biz.mailing_zip_code} />
        </Section>
      )}

      {(biz.additional_addresses || []).length > 0 && (
        <Section title="Additional Addresses">
          {biz.additional_addresses.map((a, i) => (
            <div key={i} className="md:col-span-2 rounded p-2" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <p className="text-xs font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{a.label || `Address #${i + 1}`}</p>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {[a.street_address, a.city, a.state, a.zip_code].filter(Boolean).join(', ')}
              </p>
            </div>
          ))}
        </Section>
      )}

      <Section title="Principal Owner">
        <Field label="Name" value={owner.name} />
        <Field label="Title" value={owner.title} />
        <Field label="Email" value={owner.email} />
        <Field label="SSN (last 4)" value={owner.ssn_last4 ? `••••${owner.ssn_last4}` : ''} />
        <Field label="Home Address" value={[owner.home_address, owner.home_city, owner.home_state, owner.home_zip].filter(Boolean).join(', ')} />
        <Field label="Home Phone" value={owner.home_phone} />
      </Section>

      <Section title="Bank Reference">
        <Field label="Bank" value={bank.bank_name} />
        <Field label="Bank Address" value={bank.bank_address} />
        <Field label="Account (last 4)" value={bank.account_number_last4 ? `••••${bank.account_number_last4}` : ''} />
        <Field label="Contact" value={bank.contact_name} />
        <Field label="Phone" value={bank.phone} />
        <Field label="Fax" value={bank.fax} />
      </Section>

      {refs.filter(r => r && r.name).length > 0 && (
        <Section title="Credit References">
          {refs.filter(r => r && r.name).map((r, i) => (
            <div key={i} className="md:col-span-2 rounded p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{r.name}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{r.address}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Acct: {r.account_number || '—'} · {r.phone_email || ''}
              </p>
            </div>
          ))}
        </Section>
      )}

      <Section title="Tax Status">
        <Field label="Taxable" value={tax.is_taxable ? 'Yes' : 'No / Exempt'} />
        <Field label="Tax Exemption Number" value={tax.tax_exemption_number} />
      </Section>

      <Section title="Signature">
        <Field label="Printed Name" value={sig.printed_name} />
        <Field label="Title" value={sig.title} />
        <Field label="Date" value={sig.date} />
        <Field label="Agreed to Terms" value={sig.agreed_to_terms ? '✓ Yes' : 'No'} />
      </Section>

      {docs.length > 0 && (
        <div className="rounded-lg p-4" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
          <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--primary)' }}>Uploaded Documents</p>
          <ul className="text-sm space-y-1" style={{ color: 'var(--text-muted)' }}>
            {docs.map((d, i) => <li key={i}>• {d.filename || d.name || `Document ${i+1}`}</li>)}
          </ul>
        </div>
      )}

      {/* Payment Methods */}
      <div className="rounded-lg p-4" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
        <p className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: 'var(--primary)' }}>
          <Banknote size={14} /> Payment Methods on File
        </p>
        <PaymentMethodsBlock methods={appData.payment_methods || []} />
      </div>
    </div>
  );
}
